import React, { createContext, useState, useContext, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { createTheme, ThemeProvider as MUIThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { ptBR } from "@mui/material/locale";

const ThemeContext = createContext();

// All tokens sourced from COLOR_SPEC.md
const tokens = {
  light: {
    brandMain:     "#1A6E2E",
    brandAction:   "#25A244",
    brandSurface:  "#E8F5EC",
    tealMain:      "#0F7B6C",
    tealAction:    "#15A090",
    tealSurface:   "#E0F5F3",
    bgPage:        "#F5F7F5",
    bgCard:        "#FFFFFF",
    bgDivider:     "#EEF0EE",
    border:        "#D4E0D5",
    borderStrong:  "#B0C4B4",
    textPrimary:   "#1A2E1C",
    textSecondary: "#4A6E4F",
    textMuted:     "#7A9A7E",
    amber:         "#D4821A",
    amberSurface:  "#FDF3E3",
    red:           "#C0392B",
    redSurface:    "#FDECEA",
  },
  dark: {
    brandMain:     "#00A884",
    brandAction:   "#00CF9D",
    brandSurface:  "#025144",
    tealMain:      "#00A884",
    tealAction:    "#00CF9D",
    tealSurface:   "#025144",
    bgPage:        "#0B141A",
    bgCard:        "#111B21",
    bgDivider:     "#222D35",
    border:        "#222D35",
    borderStrong:  "#2A3942",
    textPrimary:   "#E9EDEF",
    textSecondary: "#8696A0",
    textMuted:     "#667781",
    amber:         "#E0930A",
    amberSurface:  "#2E2210",
    red:           "#E84040",
    redSurface:    "#2E1212",
  },
};

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");
  const [locale, setLocale] = useState({});

  useEffect(() => {
    const i18nlocale = localStorage.getItem("i18nextLng");
    if (i18nlocale) {
      const browserLocale =
        i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5);
      if (browserLocale === "ptBR") setLocale(ptBR);
    }
  }, []);

  const toggleTheme = () => setDarkMode((prev) => {
    const next = !prev;
    localStorage.setItem("darkMode", next);
    return next;
  });

  const theme = useMemo(() => {
    const t = darkMode ? tokens.dark : tokens.light;
    return createTheme(
      {
        // Custom token bag — accessible as theme.customColors in makeStyles
        customColors: t,
        scrollbarStyles: {
          "&::-webkit-scrollbar": { width: "8px", height: "8px" },
          "&::-webkit-scrollbar-thumb": {
            boxShadow: "inset 0 0 6px rgba(0,0,0,0.3)",
            backgroundColor: t.brandMain,
          },
        },
        palette: {
          mode: darkMode ? "dark" : "light",
          primary: {
            main:         t.brandMain,
            light:        t.brandAction,
            dark:         darkMode ? "#017561" : "#0F4A1C",
            contrastText: "#FFFFFF",
          },
          secondary: {
            main:         t.tealMain,
            light:        t.tealAction,
            dark:         darkMode ? "#017561" : "#095548",
            contrastText: "#FFFFFF",
          },
          background: {
            default: t.bgPage,
            paper:   t.bgCard,
          },
          text: {
            primary:   t.textPrimary,
            secondary: t.textSecondary,
            disabled:  t.textMuted,
          },
          divider: t.bgDivider,
          warning: {
            main:        t.amber,
            light:       t.amberSurface,
            contrastText: "#FFFFFF",
          },
          error: {
            main:        t.red,
            light:       t.redSurface,
            contrastText: "#FFFFFF",
          },
          success: {
            main:  t.brandAction,
            light: t.brandSurface,
          },
        },
      },
      locale
    );
  }, [darkMode, locale]);

  const contextValue = useMemo(() => ({ darkMode, toggleTheme }), [darkMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
ThemeProvider.propTypes = { children: PropTypes.node.isRequired };

export const useThemeContext = () => useContext(ThemeContext);
